"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const heroImages = [
  "/images/mainhero/1.webp",
  "/images/mainhero/2.webp",
  "/images/mainhero/3.webp",
  "/images/mainhero/4.webp",
  "/images/mainhero/5.webp",
  "/images/mainhero/6.webp",
];

const AUTO_SWIPE_INTERVAL = 5000;
const IMAGE_BRIGHTNESS = 0.8;

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: {
    x: "0%",
    opacity: 0.6,
  },
  exit: (direction) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
  }),
};

const HeroSection = () => {
  const [[index, direction], setIndex] = useState([0, 0]);
  const [isMobile, setIsMobile] = useState(false);

  const imageIndex =
    ((index % heroImages.length) + heroImages.length) % heroImages.length;

  const paginate = (newDirection) => {
    setIndex([index + newDirection, newDirection]);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => paginate(1), AUTO_SWIPE_INTERVAL);
    return () => clearInterval(timer);
  }, [index]);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-black">
      <AnimatePresence custom={direction} initial={false}>
        <motion.img
          key={imageIndex}
          src={heroImages[imageIndex]}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: "spring", stiffness: 80, damping: 20 },
            opacity: { duration: 0.8 },
          }}
          drag={isMobile ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.8}
          onDragEnd={(e, { offset }) => {
            if (!isMobile) return;
            if (offset.x > 120) paginate(-1);
            else if (offset.x < -120) paginate(1);
          }}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: `brightness(${IMAGE_BRIGHTNESS})` }}
        />
      </AnimatePresence>

      {/* LEFT NAV — DESKTOP ONLY */}
      <button
        onClick={() => paginate(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-30 p-3 bg-black/40 hover:bg-black/60 transition rounded-full backdrop-blur"
        aria-label="Previous image"
      >
        <span className="text-white text-xl">‹</span>
      </button>

      {/* RIGHT NAV — DESKTOP ONLY */}
      <button
        onClick={() => paginate(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 p-3 bg-black/40 hover:bg-black/60 transition rounded-full backdrop-blur"
        aria-label="Next image"
      >
        <span className="text-white text-xl">›</span>
      </button>

      {/* LOGO — DESKTOP */}
      <motion.div
        className="absolute inset-0 z-20 hidden md:flex items-center justify-center pointer-events-none"
        initial={{ opacity: 0, scale: 2.8 }}
        animate={{ opacity: 1, scale: 3 }}
        transition={{
          delay: 0.6,
          duration: 1.6,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <img src="/images/LOGO.svg" alt="Studio Logo" className="w-48 md:w-64 lg:w-80" />
      </motion.div>

      {/* LOGO — MOBILE */}
      <motion.div
        className="absolute inset-0 z-20 flex md:hidden items-center justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }}
      >
        <img src="/images/LOGO.svg" alt="Studio Logo" className="w-41" />
      </motion.div>

      {/* INDICATORS */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex([i, i > imageIndex ? 1 : -1])}
            className={`w-10 h-[4px] transition-all duration-300 ${
              i === imageIndex ? "bg-white" : "bg-white/30 hover:bg-white/50"
            }`}
          />
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" />
    </section>
  );
};

export default HeroSection;
